import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { from, interval, merge, Observable, of, Subject, timer, zip } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, map, refCount, share, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';

import { GetActiveOrderQuery, GetCartTotalsQuery } from '../../../common/generated-types';
import { DataService } from '../../providers/data/data.service';
import { StateService } from '../../providers/state/state.service';

import { GET_CART_TOTALS } from './cart-toggle.graphql';
import { GET_ACTIVE_ORDER } from '../../providers/active/active.service.graphql';
import { retry } from 'ts-retry-promise';
import { isPlatformBrowser } from '@angular/common';
import { NGXLogger } from 'ngx-logger';
import { FetchPolicy } from '@apollo/client';
import { PromoService } from '../../providers/promo/promo.service';
import { NotificationService } from '../../providers/notification/notification.service';
import { APPLY_COUPON, REMOVE_COUPON } from '../../../common/graphql/documents.graphql';

@Component({
    selector: 'vsf-cart-toggle',
    templateUrl: './cart-toggle.component.html',
    styleUrls: ['./cart-toggle.component.scss'],
})
export class CartToggleComponent implements OnInit, OnDestroy {
    @Output() toggle = new EventEmitter<void>();
    activeOrderId: string | null = null;
    isQueryValid: boolean = false;
    cart$: Observable<{ total: number; quantity: number; }>;
    cartChangeIndication$: Observable<boolean>;
    private static readonly MAX_QUERY_RETRIES = 15;
    private static readonly QUERY_INTERVAL = 200;
    private static readonly CART_CHANGE_DEBOUNCE_MS = 100;
    private destroy$: Subject<void> = new Subject<void>();
    constructor(@Inject(PLATFORM_ID) private platformId: object,
                private dataService: DataService,
                private stateService: StateService,
                private promoService: PromoService,
                private notificationService: NotificationService,
                private logger: NGXLogger) {
    }

    ngOnInit() {
        this.cart$ = merge(
            this.stateService.select(state => state.activeOrderId),
            this.stateService.select(state => state.signedIn),
        ).pipe(
            debounceTime(CartToggleComponent.CART_CHANGE_DEBOUNCE_MS),
            switchMap(() => this.dataService.query<GetCartTotalsQuery>(GET_CART_TOTALS, {}, 'network-only')),
            map(({activeOrder}) => {
                if(activeOrder?.active) {
                    this.applyPendingCoupons();
                    return {
                        total: activeOrder ? activeOrder.totalWithTax : 0,
                        quantity: activeOrder ? activeOrder.totalQuantity : 0,
                    };
                } else {
                    return {
                        total: 0,
                        quantity: 0,
                    };
                }
            }),
            shareReplay(1),
            takeUntil(this.destroy$)
        );
        this.stateService.select(state => state.activeOrderId).pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((activeOrderId) => {
            this.activeOrderId = activeOrderId;
        });
        this.cartChangeIndication$ = this.cart$.pipe(
            map(cart => cart.quantity),
            distinctUntilChanged(),
            switchMap(() => zip(
                    from([true, false]),
                    timer(0, 1000),
                ).pipe(map(([val]) => val)),
            ),
        );
        this.stateService.select(state => state.clientInitState).pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe((clientInitState) => {
            if (isPlatformBrowser(this.platformId) && !clientInitState) {
                this.fetchActiveOrderWithRetry().then(() => {
                    this.stateService.setState('clientInitState', true);
                });
            }
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async applyPendingCoupons(): Promise<void> {
        const pending = [...this.promoService.active];
        if (pending.length === 0) return;
        let { activeOrder } = (await this.dataService.query<GetActiveOrderQuery>(GET_ACTIVE_ORDER, {}, 'network-only').pipe(take(1)).toPromise())!;
        for (const promo of pending) {
            try {
            const currentTotalDiscount = this.calculateCurrentTotalDiscount(activeOrder);
            const { applyCouponCode } = await this.dataService.mutate(APPLY_COUPON, { couponCode: promo.couponCode })
                .pipe(take(1)).toPromise();

            if (applyCouponCode?.__typename === 'Order') {
                const updatedTotalDiscount =this.calculateCurrentTotalDiscount(applyCouponCode);
                if (updatedTotalDiscount === currentTotalDiscount) {
                await this.dataService.mutate(REMOVE_COUPON, { couponCode: promo.couponCode }).pipe(take(1)).toPromise();
                this.promoService.markAdded(promo.couponCode);
                this.logger.warn(`Coupon ${promo.couponCode} removed - no eligible items.`);
                } else {
                this.promoService.markApplied(promo.couponCode);
                await this.notificationService.success(`Promo code "${promo.couponCode}" applied.`).pipe(take(1)).subscribe();
                this.logger.info(`Coupon ${promo.couponCode} applied`);
                }
                activeOrder = applyCouponCode;
            } else {
                this.logger.warn(`Coupon ${promo.couponCode} failed: ${(applyCouponCode as any).message}`);
            }
            } catch (err) {
            this.logger.error(`Coupon ${promo.couponCode} failed with error`, err);
            }
        }
    }

    private calculateCurrentTotalDiscount(order: any): number {
        let totalDiscount = 0;
        if (order && order.discounts && order.discounts.length > 0) {
          order.discounts.forEach((discount: any) => {
            if (discount?.amountWithTax) {
              totalDiscount += discount?.amountWithTax || 0;
            }
          });
        }
        return totalDiscount;
    }

    private fetchActiveOrder(type = 'cache-first') {
        this.dataService.query<GetActiveOrderQuery>(GET_ACTIVE_ORDER, {}, type as FetchPolicy)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe(data => {
            if(data?.activeOrder) {
                this.stateService.setState('activeOrderId', data?.activeOrder.id);
                this.activeOrderId = data?.activeOrder.id;
                this.logger.debug(`[${new Date().toISOString()}][cart-toggle.component] fetched activeOrder.`);
            }
            this.isQueryValid = true;
        });
    }

    async fetchActiveOrderWithRetry() {
        try {
            await retry(
                async() => this.fetchActiveOrder('network-only'),
                {
                    retries: CartToggleComponent.MAX_QUERY_RETRIES,
                    delay: CartToggleComponent.QUERY_INTERVAL,
                    until: () => this.isQueryValid,
                }
            );
        } catch (error) {
            this.logger.error(`[${new Date().toISOString()}][cart-toggle.component] Fetching after max retries.`);
        }
    }

}
